/**
 * Typographic layer — renders catalog text in three bands flanking the
 * ASCII canvas.
 *
 * For entry scenes:
 *   left band   → roman numeral, title, provenance
 *   right band  → description, citations, marginalia
 *   center band → empty (the painting takes the middle)
 *
 * For preface and coda scenes:
 *   left + right → empty
 *   center band  → full-width centered prose (no painting to flank)
 *
 * The bands are semi-transparent overlays on the ASCII canvas; they
 * fade in synchronously after the painting reveals.
 */

export function initTypographicLayer({ leftBand, rightBand, centerBand }) {
  return new TypographicLayer({ leftBand, rightBand, centerBand });
}

class TypographicLayer {
  constructor({ leftBand, rightBand, centerBand }) {
    this.left = leftBand;
    this.right = rightBand;
    this.center = centerBand;
  }

  async renderScene(scene, { revealMs = 0 } = {}) {
    if (scene.type === "entry") {
      this.left.innerHTML = this._composeEntryHeader(scene);
      this.right.innerHTML = this._composeEntryBody(scene);
      this.center.innerHTML = "";
    } else if (scene.type === "preface" || scene.type === "coda") {
      this.left.innerHTML = "";
      this.right.innerHTML = "";
      this.center.innerHTML = this._composeProseScene(scene);
    } else {
      this.left.innerHTML = "";
      this.right.innerHTML = "";
      this.center.innerHTML = "";
    }

    if (revealMs > 0) {
      // Fade in the band wrappers so background gradients appear with the
      // reveal, but type the English title in over ~650ms after a brief
      // beat for a more cinematic entrance. Other text fades normally.
      if (scene.type === "entry") {
        const titleEl = this.left.querySelector(".entry-title");
        const fullTitle = titleEl ? titleEl.textContent : "";
        if (titleEl) titleEl.textContent = "";

        // Kick off both reveals; fade resolves on its own timing.
        this._fadeIn(revealMs);
        if (titleEl && fullTitle) {
          // Brief delay so the title types in *after* the band gradient
          // and other elements are ~half-visible.
          await this._sleep(Math.min(revealMs * 0.4, 500));
          await this._typeText(titleEl, fullTitle, 35);
        } else {
          await this._sleep(revealMs);
        }
      } else {
        await this._fadeIn(revealMs);
      }
    } else {
      this._setOpacity(1);
    }
  }

  _sleep(ms) {
    return new Promise((r) => setTimeout(r, ms));
  }

  async _typeText(el, text, msPerChar = 35) {
    el.textContent = "";
    for (let i = 0; i < text.length; i++) {
      el.textContent += text[i];
      await this._sleep(msPerChar);
    }
  }

  _composeEntryHeader(scene) {
    const originalTitle = scene.originalTitle
      ? `<div class="original-title">${scene.originalTitle}</div>`
      : "";
    // The Roman numeral now lives INSIDE the entry-title h2 so portrait
    // CSS can render it inline ("I. The Owl-Glass") and reclaim a row of
    // vertical space — the citation block needs to fit alongside the
    // painting on iPhone 17 Pro Max. Desktop CSS still treats the
    // .roman-numeral span as `display: block` for the museum-label feel.
    const numeral = scene.numeral
      ? `<span class="roman-numeral">${scene.numeral}</span>`
      : "";
    return `
      <div class="band-content entry-header">
        ${originalTitle}
        <h2 class="entry-title">${numeral}${numeral ? " " : ""}${scene.title ?? ""}</h2>
        <div class="provenance">${scene.provenance ?? ""}</div>
      </div>
    `;
  }

  _composeEntryBody(scene) {
    const citations = (scene.citations || [])
      .map(
        (c) =>
          `<blockquote>${c.quote}<span class="citation">— ${c.source}</span></blockquote>`,
      )
      .join("");
    const marginalia = scene.marginalia
      ? `<p class="marginalia">${scene.marginalia}</p>`
      : "";
    // Length-based density class — entries with descriptions over
    // ~1100 chars (currently just VII Christ Disputing at 1190)
    // overflow the desktop right column at the standard body size
    // and clip behind the corner nav pill. The .entry-body-dense
    // class triggers a slightly smaller font in the desktop CSS.
    // Threshold sits between V (1060) and VII (1190) so future
    // mid-length entries don't auto-shrink unintentionally.
    const desc = scene.description || "";
    const denseClass = desc.length > 1100 ? " entry-body-dense" : "";
    return `
      <div class="band-content entry-body${denseClass}">
        <p class="description">${desc}</p>
        ${citations}
        ${marginalia}
      </div>
    `;
  }

  _composeProseScene(scene) {
    // Frontispiece — title-page treatment at the top of the preface.
    // Not used for coda, which only has the section header.
    const volumeMark = scene.volumeMark
      ? `<div class="prose-volume">${scene.volumeMark}</div>`
      : "";
    // Split the frontispiece title into its first word + the rest, so
    // portrait CSS can render it as a two-line frontispiece:
    //   line 1:  BESTIARY        (caps, full-width)
    //   line 2:  of the Lost     (italic, width-matched)
    // Desktop CSS leaves both spans inline, so the title still reads
    // "Bestiary of the Lost" in italic serif as before.
    let titleEn = "";
    if (scene.titleEn) {
      const firstSpace = scene.titleEn.indexOf(" ");
      if (firstSpace > 0) {
        const main = scene.titleEn.slice(0, firstSpace);
        const tail = scene.titleEn.slice(firstSpace + 1);
        titleEn = `<h1 class="prose-title"><span class="prose-title-main">${main}</span> <span class="prose-title-tail">${tail}</span></h1>`;
      } else {
        titleEn = `<h1 class="prose-title"><span class="prose-title-main">${scene.titleEn}</span></h1>`;
      }
    }
    const titleLat = scene.titleLat
      ? `<div class="prose-subtitle">${scene.titleLat}</div>`
      : "";
    const frontispiece =
      volumeMark || titleEn || titleLat
        ? `<div class="prose-frontispiece">${volumeMark}${titleEn}${titleLat}</div>`
        : "";
    const header = scene.header
      ? `<h2 class="prose-header">${scene.header}</h2>`
      : "";
    const epigraph = scene.epigraph
      ? `<div class="prose-epigraph">${scene.epigraph}</div>`
      : "";
    // The `--p-idx` custom property is read by the CSS to stagger
    // each paragraph's fade-in, so the page reads top-down rather
    // than appearing all at once.
    const paragraphs = (scene.body || "")
      .split(/\n\s*\n/)
      .map((p, i) => `<p style="--p-idx: ${i}">${p.trim()}</p>`)
      .join("");
    const signature = scene.signature
      ? `<div class="prose-signature">${scene.signature}</div>`
      : "";
    const dateline =
      scene.place || scene.date
        ? `<div class="prose-dateline">${[scene.place, scene.date]
            .filter(Boolean)
            .join(" · ")}</div>`
        : "";
    return `<div class="band-content prose-block ${scene.type}">${frontispiece}${header}${epigraph}${paragraphs}${signature}${dateline}</div>`;
  }

  _setOpacity(value) {
    this.left.style.opacity = value;
    this.right.style.opacity = value;
    this.center.style.opacity = value;
  }

  async _fadeIn(durationMs) {
    const t = `opacity ${durationMs}ms ease-in`;
    this.left.style.transition = t;
    this.right.style.transition = t;
    this.center.style.transition = t;
    this._setOpacity(0);
    // Two rAFs guarantee the browser commits the 0 state before we
    // write 1. The `void offsetWidth` reflow trick is unreliable
    // because modern engines coalesce back-to-back style writes
    // before the next paint, which silently drops the transition
    // and leaves opacity stuck at 0.
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
    this._setOpacity(1);
    await new Promise((r) => setTimeout(r, durationMs));
    // Final safety net: if the transition was stripped (e.g.
    // prefers-reduced-motion's `transition: none !important`) or
    // the tab was throttled mid-fade, ensure the final state is
    // visible regardless.
    this._setOpacity(1);
  }

  async fadeOut({ durationMs = 1200 } = {}) {
    const t = `opacity ${durationMs}ms ease-out`;
    this.left.style.transition = t;
    this.right.style.transition = t;
    this.center.style.transition = t;
    this._setOpacity(0);
    await new Promise((r) => setTimeout(r, durationMs));
  }
}
